import React from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { getGetFacesQueryOptions, getGetFaceCountQueryOptions, useGetCard } from '@vlabs/api/point-api/client';
import { SearchByExternalIdButton } from '@vlabs/monokit/features';
import { CreateCardFace } from '@vlabs/monokit/features/create-face';
import { useCardIdFromLocation } from '@vlabs/monokit/shared/lib/use-card-id-from-location';
import { buildTestid } from '@vlabs/monokit/shared/lib/utils';
import { PageHeader, usePageContext } from '@vlabs/monokit/shared/ui/page';
import { routes } from '@vlabs/shared/navigation/routes';
import { Breadcrumbs, Breadcrumb } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';

import { CardPageFilterMenuButton } from '../card-page-filter-menu-button';
import type { TPointCard } from '../types';

const displayName = 'CardPageHeader';
const testid = buildTestid(displayName);

function CardPageHeader(): React.JSX.Element {
  const cardId = useCardIdFromLocation();
  const { t } = useTranslation();
  const getCard = useGetCard<TPointCard>(cardId);
  const queryClient = useQueryClient();
  const pageCtx = usePageContext();

  const cardListOptions = (getCard.data?.lists ?? []).map((listId) => ({
    label: listId,
    value: listId,
  }));

  const breadcrumbs = (
    <Breadcrumbs>
      <Breadcrumb
        caption={t('card-page:breadcrumbs.cards')}
        href={generatePath(routes.cards.index)}
      />
      <Breadcrumb caption={t('card-page:breadcrumbs.card_page')} />
    </Breadcrumbs>
  );

  return (
    <PageHeader
      {...testid()}
      actions={(
        <React.Fragment>
          <CreateCardFace
            context="card"
            onSuccess={() => {
              queryClient.invalidateQueries(getGetFacesQueryOptions({ card_ids: [cardId] }));
              queryClient.invalidateQueries(getGetFaceCountQueryOptions({ card_ids: [cardId] }));
            }}
            size={pageCtx.buttonSize}
            title={t('face') as unknown as string}
            defaultValues={{ external_id: cardId, lists: cardListOptions }}
            // FIXME: выкинуть эту шляпу, задача https://jira.visionlabs.ru/browse/GUI-3390
            noSelect
          />
          <SearchByExternalIdButton
            externalId={cardId}
            size={pageCtx.buttonSize}
          />
          <CardPageFilterMenuButton />
        </React.Fragment>
      )}
      breadcrumbs={breadcrumbs}
      title={t('card-page:page_title', { fio: getCard.data?.user_data?.fio ?? '-' })}
    />
  );
}

export { CardPageHeader };
